import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../../Config';
import {
  format,
  parseISO,
  isValid,
  formatDistanceToNow,
  subDays,
  startOfDay,
  endOfDay
} from 'date-fns';
import { fr } from 'date-fns/locale';

// Icônes
import {
  FiPlus,
  FiEye,
  FiDownload,
  FiFilter,
  FiRefreshCw,
  FiX,
  FiCheck,
  FiCalendar,
  FiUser,
  FiCheckCircle,
  FiXCircle,
  FiTruck,
  FiArrowLeft,
  FiSearch,
  FiChevronDown,
  FiChevronRight
} from 'react-icons/fi';
import {
  HiOutlineExclamationCircle,
  HiOutlineCollection,
  HiOutlineCalendar,
} from 'react-icons/hi';
import { RiHistoryLine } from 'react-icons/ri';

const HistoriquesPage = () => {
  const [historiques, setHistoriques] = useState([]);
  const [evenements, setEvenements] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedHistorique, setSelectedHistorique] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState({
    evenement_id: '',
    user_id: '',
    action: '',
    startDate: '',
    endDate: '',
    searchTerm: ''
  });
  const [timeRange, setTimeRange] = useState('all');
  const [formData, setFormData] = useState({
    evenement_id: '',
    action: 'validation',
    user_id: ''
  });
  const [errors, setErrors] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const api = axios.create({
    baseURL: API_URL,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    }
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [historiquesRes, evenementsRes, usersRes] = await Promise.all([
        api.get('/historiques/evenement'),
        api.get('/evenements'),
        api.get('/users').catch(() => ({ data: [] })) // Gérer l'erreur silencieusement
      ]);

      setHistoriques(extractArrayFromResponse(historiquesRes.data));
      setEvenements(extractArrayFromResponse(evenementsRes.data));
      setUsers(extractArrayFromResponse(usersRes.data));

    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  };

  const extractArrayFromResponse = (data) => {
    if (Array.isArray(data)) return data;
    if (data && data.data && Array.isArray(data.data)) return data.data;
    if (data && typeof data === 'object') return Object.values(data);
    return [];
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
    
    const now = new Date();
    let startDate = '';
    let endDate = '';

    switch (range) {
      case 'today':
        startDate = format(startOfDay(now), 'yyyy-MM-dd');
        endDate = format(endOfDay(now), 'yyyy-MM-dd');
        break;
      case 'yesterday':
        const yesterday = subDays(now, 1);
        startDate = format(startOfDay(yesterday), 'yyyy-MM-dd');
        endDate = format(endOfDay(yesterday), 'yyyy-MM-dd');
        break;
      case 'week':
        startDate = format(startOfDay(subDays(now, 7)), 'yyyy-MM-dd');
        endDate = format(endOfDay(now), 'yyyy-MM-dd');
        break;
      case 'month':
        startDate = format(startOfDay(subDays(now, 30)), 'yyyy-MM-dd');
        endDate = format(endOfDay(now), 'yyyy-MM-dd');
        break;
      default:
        // 'all' - no date filters
        break;
    }

    setFilters(prev => ({
      ...prev,
      startDate,
      endDate
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.evenement_id) {
      newErrors.evenement_id = "L'événement est requis";
    }
    
    if (!formData.action) {
      newErrors.action = "L'action est requise";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    try {
      const response = await api.post('/historiques', formData);
      setHistoriques(prev => [response.data, ...prev]);
      showSnackbar('Historique créé avec succès', 'success');
      resetForm();
      setOpenCreateDialog(false);
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors de la création';
      showSnackbar(message, 'error');
    }
  };

  const handleViewDetails = (historique) => {
    setSelectedHistorique(historique);
    setOpenDetailsDialog(true);
  };

  const resetForm = () => {
    setFormData({
      evenement_id: '',
      action: 'validation',
      user_id: ''
    });
    setErrors({});
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const getActionConfig = (action) => {
    const configs = {
      validation: {
        icon: <FiCheckCircle className="w-4 h-4" />,
        label: 'Validation',
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        dot: 'bg-emerald-500'
      },
      refus: {
        icon: <FiXCircle className="w-4 h-4" />,
        label: 'Refus',
        color: 'text-red-600',
        bg: 'bg-red-50',
        border: 'border-red-200',
        dot: 'bg-red-500'
      },
      livraison: {
        icon: <FiTruck className="w-4 h-4" />,
        label: 'Livraison',
        color: 'text-blue-600',
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        dot: 'bg-blue-500'
      },
      retour: {
        icon: <FiArrowLeft className="w-4 h-4" />,
        label: 'Retour',
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        dot: 'bg-amber-500'
      }
    };
    return configs[action] || { 
      icon: <RiHistoryLine className="w-4 h-4" />,
      label: action,
      color: 'text-gray-600',
      bg: '',
      border: 'border-gray-200',
      dot: '0'
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) return dateString;
      return format(date, 'dd MMMM yyyy HH:mm:ss', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) return '';
      return formatDistanceToNow(date, { addSuffix: true, locale: fr });
    } catch (error) {
      return '';
    }
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = parseISO(dateString);
      if (!isValid(date)) return dateString;
      return format(date, 'dd/MM/yyyy', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };

  // Fonctions auxiliaires à définir avant le useMemo
  const getEvenementInfo = (evenementId) => {
    return evenements.find(e => e.id === evenementId);
  };

  const getUserInfo = (userId) => {
    return users.find(u => u.id === userId);
  };

  const filteredHistoriques = useMemo(() => {
    let filtered = historiques.filter(historique => {
      const evenement = getEvenementInfo(historique.evenement_id);
      const user = getUserInfo(historique.user_id);
      
      // Filtre de recherche
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const matchesSearch = 
          historique.id.toString().includes(searchLower) ||
          (evenement?.type?.toLowerCase().includes(searchLower)) ||
          (user?.name?.toLowerCase().includes(searchLower)) ||
          getActionConfig(historique.action).label.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      if (filters.evenement_id && historique.evenement_id != filters.evenement_id) return false;
      if (filters.user_id && historique.user_id != filters.user_id) return false;
      if (filters.action && historique.action !== filters.action) return false;
      
      if (filters.startDate) {
        const histDate = parseISO(historique.created_at);
        const startDate = parseISO(filters.startDate);
        if (histDate < startDate) return false;
      }
      
      if (filters.endDate) {
        const histDate = parseISO(historique.created_at);
        const endDate = parseISO(filters.endDate);
        if (histDate > endDate) return false;
      }
      
      return true;
    });

    // Tri
    filtered.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [historiques, filters, sortConfig, evenements, users]); // Ajout des dépendances manquantes

  const getStats = useMemo(() => {
    const stats = {
      total: historiques.length,
      validation: historiques.filter(h => h.action === 'validation').length,
      refus: historiques.filter(h => h.action === 'refus').length,
      livraison: historiques.filter(h => h.action === 'livraison').length,
      retour: historiques.filter(h => h.action === 'retour').length
    };
    
    // Pourcentages
    stats.validationPercent = stats.total > 0 ? Math.round((stats.validation / stats.total) * 100) : 0;
    stats.refusPercent = stats.total > 0 ? Math.round((stats.refus / stats.total) * 100) : 0;
    stats.livraisonPercent = stats.total > 0 ? Math.round((stats.livraison / stats.total) * 100) : 0;
    stats.retourPercent = stats.total > 0 ? Math.round((stats.retour / stats.total) * 100) : 0;
    
    return stats;
  }, [historiques]);

  const exportToCSV = () => {
    const csvContent = [
      ['ID', 'Événement ID', 'Type Événement', 'Utilisateur', 'Action', 'Date'],
      ...filteredHistoriques.map(h => [
        h.id,
        h.evenement_id,
        getEvenementInfo(h.evenement_id)?.type || 'N/A',
        getUserInfo(h.user_id)?.name || `User #${h.user_id}`,
        getActionConfig(h.action).label,
        formatDate(h.created_at)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `historiques_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showSnackbar('Export CSV réussi', 'success');
  };

  if (loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de l'historique...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="bg-blue-50 p-2 rounded-lg mr-3">
                <RiHistoryLine className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Historique des Événements</h1>
                <p className="text-sm text-gray-500">Suivi des actions et modifications</p>
              </div>
              <span className="ml-4 bg-gray-100 text-gray-600 text-sm font-medium px-2.5 py-0.5 rounded-full">
                {historiques.length} action(s)
              </span>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={fetchData}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <FiRefreshCw className="w-4 h-4" />
                Actualiser
              </button>
              <button
                onClick={exportToCSV}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:"
              >
                <FiDownload className="w-4 h-4" />
                <span className="text-sm font-medium">Exporter</span>
              </button>
              <button
                onClick={() => setOpenCreateDialog(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <FiPlus className="w-4 h-4" />
                <span className="text-sm font-medium">Ajouter</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total des actions</p>
                <p className="text-3xl font-semibold text-gray-900 mt-2">{getStats.total}</p>
              </div>
              <div className=" p-3 rounded-lg">
                <HiOutlineCollection className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Validations</p>
                <p className="text-3xl font-semibold text-emerald-600 mt-2">{getStats.validation}</p>
                <div className="flex items-center mt-1">
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div 
                      className="bg-emerald-600 h-1.5 rounded-full" 
                      style={{ width: `${getStats.validationPercent}%` }}
                    ></div>
                  </div>
                  <span className="ml-2 text-xs text-gray-500">{getStats.validationPercent}%</span>
                </div>
              </div>
              <div className="bg-emerald-50 p-3 rounded-lg">
                <FiCheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Refus</p>
                <p className="text-3xl font-semibold text-red-600 mt-2">{getStats.refus}</p>
                <div className="flex items-center mt-1">
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div 
                      className="bg-red-600 h-1.5 rounded-full" 
                      style={{ width: `${getStats.refusPercent}%` }}
                    ></div>
                  </div>
                  <span className="ml-2 text-xs text-gray-500">{getStats.refusPercent}%</span>
                </div>
              </div>
              <div className="bg-red-50 p-3 rounded-lg">
                <FiXCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Livraisons</p>
                <p className="text-3xl font-semibold text-blue-600 mt-2">{getStats.livraison}</p>
                <div className="flex items-center mt-1">
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div 
                      className="bg-blue-600 h-1.5 rounded-full" 
                      style={{ width: `${getStats.livraisonPercent}%` }}
                    ></div>
                  </div>
                  <span className="ml-2 text-xs text-gray-500">{getStats.livraisonPercent}%</span>
                </div>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <FiTruck className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Retours</p>
                <p className="text-3xl font-semibold text-amber-600 mt-2">{getStats.retour}</p>
                <div className="flex items-center mt-1">
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div 
                      className="bg-amber-600 h-1.5 rounded-full" 
                      style={{ width: `${getStats.retourPercent}%` }}
                    ></div>
                  </div>
                  <span className="ml-2 text-xs text-gray-500">{getStats.retourPercent}%</span>
                </div>
              </div>
              <div className="bg-amber-50 p-3 rounded-lg">
                <FiArrowLeft className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex-1">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Rechercher par ID, événement, utilisateur ou action..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={filters.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center space-x-2">
                <FiFilter className="text-gray-400" />
                <div className="flex space-x-1">
                  {['all', 'today', 'yesterday', 'week', 'month'].map((range) => (
                    <button
                      key={range}
                      onClick={() => handleTimeRangeChange(range)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                        timeRange === range
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 text-gray-700 hover:'
                      }`}
                    >
                      {range === 'all' && 'Tout'}
                      {range === 'today' && 'Aujourd\'hui'}
                      {range === 'yesterday' && 'Hier'}
                      {range === 'week' && '7j'}
                      {range === 'month' && '30j'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Advanced Filters */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Événement</label>
              <select
                value={filters.evenement_id}
                onChange={(e) => handleFilterChange('evenement_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Tous les événements</option>
                {evenements.map(evenement => (
                  <option key={evenement.id} value={evenement.id}>
                    #{evenement.id} - {evenement.type}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Utilisateur</label>
              <select
                value={filters.user_id}
                onChange={(e) => handleFilterChange('user_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Tous les utilisateurs</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.nom} ({user.email})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
              <select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Toutes les actions</option>
                <option value="validation">Validation</option>
                <option value="refus">Refus</option>
                <option value="livraison">Livraison</option>
                <option value="retour">Retour</option>
              </select>
            </div>
          </div>
          
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => {
                setFilters({
                  evenement_id: '',
                  user_id: '',
                  action: '',
                  startDate: '',
                  endDate: '',
                  searchTerm: ''
                });
                setTimeRange('all');
              }}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:"
            >
              <FiX className="w-4 h-4" />
              Réinitialiser les filtres
            </button>
          </div>
        </div>

        {/* Table */}
        {filteredHistoriques.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <RiHistoryLine className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucun historique trouvé
            </h3>
            <p className="text-gray-500 mb-4">
              {filters.searchTerm || Object.values(filters).some(f => f) 
                ? 'Aucun historique ne correspond à vos critères'
                : 'Commencez par ajouter un historique'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Événement
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Utilisateur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredHistoriques.map((historique) => {
                    const actionConfig = getActionConfig(historique.action);
                    const evenement = getEvenementInfo(historique.evenement_id);
                    const user = getUserInfo(historique.user_id);
                    const isExpanded = expandedRows[historique.id];
                    
                    return (
                      <React.Fragment key={historique.id}>
                        <tr className="hover:">
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className={`${actionConfig.bg} p-2 rounded-lg`}>
                                {actionConfig.icon}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  #{historique.id} • {actionConfig.label}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  Action {actionConfig.label.toLowerCase()}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {evenement ? (
                              <div className="flex items-center space-x-3">
                                <div className="bg-blue-50 p-2 rounded-lg">
                                  <HiOutlineCalendar className="w-4 h-4 text-blue-600" />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    Événement #{evenement.id}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {evenement.type} • {evenement.statut}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-3">
                                <div className="bg-gray-100 p-2 rounded-lg">
                                  <HiOutlineCalendar className="w-4 h-4 text-gray-600" />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    Événement #{historique.evenement_id}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    Information non disponible
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {user ? (
                              <div className="flex items-center space-x-3">
                                <div className="bg-purple-50 p-2 rounded-lg">
                                  <FiUser className="w-4 h-4 text-purple-600" />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {user.nom}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {user.email}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-3">
                                <div className="bg-gray-100 p-2 rounded-lg">
                                  <FiUser className="w-4 h-4 text-gray-600" />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    Utilisateur #{historique.user_id}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    Information non disponible
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {formatShortDate(historique.created_at)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatTimeAgo(historique.created_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleViewDetails(historique)}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Voir les détails"
                              >
                                <FiEye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => toggleRowExpansion(historique.id)}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              >
                                {isExpanded ? (
                                  <FiChevronDown className="w-4 h-4" />
                                ) : (
                                  <FiChevronRight className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                        
                        {/* Row Expansion */}
                        {isExpanded && (
                          <tr>
                            <td colSpan="5" className="px-6 py-4 ">
                              <div className="pl-14">
                                <div className="bg-white rounded-lg border border-gray-200 p-4">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-700 mb-2">Détails de l'action</h4>
                                      <div className="space-y-2">
                                        <div className="flex justify-between">
                                          <span className="text-sm text-gray-500">Action:</span>
                                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${actionConfig.bg} ${actionConfig.color}`}>
                                            {actionConfig.icon}
                                            {actionConfig.label}
                                          </span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-sm text-gray-500">Date complète:</span>
                                          <span className="text-sm font-medium text-gray-900">
                                            {formatDate(historique.created_at)}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-700 mb-2">Information supplémentaire</h4>
                                      <div className="space-y-2">
                                        <div className="flex justify-between">
                                          <span className="text-sm text-gray-500">Créé il y a:</span>
                                          <span className="text-sm text-gray-900">
                                            {formatTimeAgo(historique.created_at)}
                                          </span>
                                        </div>
                                        {historique.updated_at && historique.updated_at !== historique.created_at && (
                                          <div className="flex justify-between">
                                            <span className="text-sm text-gray-500">Dernière mise à jour:</span>
                                            <span className="text-sm text-gray-900">
                                              {formatTimeAgo(historique.updated_at)}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Info */}
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Affichage de {filteredHistoriques.length} action(s)
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setSortConfig(prev => ({ 
                      ...prev, 
                      direction: prev.direction === 'asc' ? 'desc' : 'asc' 
                    }))}
                    className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
                  >
                    {sortConfig.direction === 'asc' ? (
                      <>
                        <span>Plus ancien</span>
                        <FiChevronDown className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        <span>Plus récent</span>
                        <FiChevronDown className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Dialog Création */}
      {openCreateDialog && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity 0 bg-opacity-75" aria-hidden="true"></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <FiPlus className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Ajouter un historique
                      </h3>
                      <p className="text-sm text-gray-500">
                        Enregistrer une nouvelle action
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setOpenCreateDialog(false);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-gray-500 transition-colors"
                  >
                    <FiX className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-b border-gray-200">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Événement <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="evenement_id"
                      value={formData.evenement_id}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.evenement_id ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Sélectionner un événement</option>
                      {evenements.map(evenement => (
                        <option key={evenement.id} value={evenement.id}>
                          #{evenement.id} - {evenement.type}
                        </option>
                      ))}
                    </select>
                    {errors.evenement_id && (
                      <p className="mt-1 text-sm text-red-600">{errors.evenement_id}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Action <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="action"
                      value={formData.action}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.action ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="validation">Validation</option>
                      <option value="refus">Refus</option>
                      <option value="livraison">Livraison</option>
                      <option value="retour">Retour</option>
                    </select>
                    {errors.action && (
                      <p className="mt-1 text-sm text-red-600">{errors.action}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Utilisateur (optionnel)
                    </label>
                    <select
                      name="user_id"
                      value={formData.user_id}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Utilisateur actuel</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.nom} ({user.email})
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Si vide, l'utilisateur connecté sera utilisé
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="px-6 py-4  flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setOpenCreateDialog(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover: transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreate}
                  className="px-4 py-2 bg-blue-600 rounded-lg text-sm font-medium text-white hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <FiCheck className="w-4 h-4" />
                  Créer l'historique
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dialog Détails */}
      {openDetailsDialog && selectedHistorique && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity 0 bg-opacity-75" aria-hidden="true"></div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`${getActionConfig(selectedHistorique.action).bg} p-3 rounded-lg`}>
                      {getActionConfig(selectedHistorique.action).icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Historique #{selectedHistorique.id}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {formatTimeAgo(selectedHistorique.created_at)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setOpenDetailsDialog(false)}
                    className="text-gray-400 hover:text-gray-500 transition-colors"
                  >
                    <FiX className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-b border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Informations principales */}
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Détails de l'action</h4>
                      <div className=" rounded-lg p-4">
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-500">Action:</span>
                            <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${getActionConfig(selectedHistorique.action).bg} ${getActionConfig(selectedHistorique.action).color}`}>
                              {getActionConfig(selectedHistorique.action).icon}
                              {getActionConfig(selectedHistorique.action).label}
                            </span>
                          </div>
                          
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Date et heure:</span>
                            <span className="text-sm font-medium text-gray-900">
                              {formatDate(selectedHistorique.created_at)}
                            </span>
                          </div>
                          
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Il y a:</span>
                            <span className="text-sm text-gray-900">
                              {formatTimeAgo(selectedHistorique.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Événement */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Événement concerné</h4>
                      {getEvenementInfo(selectedHistorique.evenement_id) ? (
                        <div className="bg-blue-50 rounded-lg p-4">
                          <div className="flex items-start space-x-3">
                            <div className="bg-blue-100 p-2 rounded-lg">
                              <HiOutlineCalendar className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="space-y-1">
                              <h5 className="text-sm font-medium text-gray-900">
                                Événement #{selectedHistorique.evenement_id}
                              </h5>
                              <div className="space-y-1">
                                <p className="text-sm text-gray-600">
                                  Type: {getEvenementInfo(selectedHistorique.evenement_id).type}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Statut: {getEvenementInfo(selectedHistorique.evenement_id).statut}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className=" rounded-lg p-4 text-center">
                          <p className="text-sm text-gray-500">
                            Événement #{selectedHistorique.evenement_id}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Utilisateur */}
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Utilisateur</h4>
                      {getUserInfo(selectedHistorique.user_id) ? (
                        <div className="bg-purple-50 rounded-lg p-4">
                          <div className="flex items-start space-x-3">
                            <div className="bg-purple-100 p-2 rounded-lg">
                              <FiUser className="w-5 h-5 text-purple-600" />
                            </div>
                            <div className="space-y-1">
                              <h5 className="text-sm font-medium text-gray-900">
                                {getUserInfo(selectedHistorique.user_id).nom}
                              </h5>
                              <div className="space-y-1">
                                <p className="text-sm text-gray-600">
                                  Email: {getUserInfo(selectedHistorique.user_id).email}
                                </p>
                                <p className="text-sm text-gray-600">
                                  ID: {getUserInfo(selectedHistorique.user_id).id}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className=" rounded-lg p-4 text-center">
                          <p className="text-sm text-gray-500">
                            Utilisateur #{selectedHistorique.user_id}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Métadonnées */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Informations techniques</h4>
                      <div className=" rounded-lg p-4">
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">ID historique:</span>
                            <span className="text-sm font-medium text-gray-900">#{selectedHistorique.id}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-500">Créé le:</span>
                            <span className="text-sm text-gray-900">{formatShortDate(selectedHistorique.created_at)}</span>
                          </div>
                          {selectedHistorique.updated_at && selectedHistorique.updated_at !== selectedHistorique.created_at && (
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-500">Mis à jour:</span>
                              <span className="text-sm text-gray-900">{formatShortDate(selectedHistorique.updated_at)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="px-6 py-4  flex justify-end space-x-3">
                <button
                  onClick={() => setOpenDetailsDialog(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover: transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar */}
      {snackbar.open && (
        <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
          <div className={`rounded-lg shadow-lg p-4 max-w-md transform transition-all duration-300 ${
            snackbar.severity === 'success' 
              ? 'bg-emerald-50 border border-emerald-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-start">
              <div className={`flex-shrink-0 ${
                snackbar.severity === 'success' ? 'text-emerald-400' : 'text-red-400'
              }`}>
                {snackbar.severity === 'success' ? (
                  <FiCheckCircle className="w-5 h-5" />
                ) : (
                  <HiOutlineExclamationCircle className="w-5 h-5" />
                )}
              </div>
              <div className="ml-3 flex-1">
                <p className={`text-sm font-medium ${
                  snackbar.severity === 'success' ? 'text-emerald-800' : 'text-red-800'
                }`}>
                  {snackbar.message}
                </p>
              </div>
              <button
                onClick={() => setSnackbar(prev => ({ ...prev, open: false }))}
                className="ml-4 flex-shrink-0"
              >
                <FiX className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoriquesPage;